import { Head, useForm, usePage } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import { companyProfile } from "@/data/marketing-site";
import { support_path } from "@/routes";

const supportChannels = [
  {
    label: "E-mail",
    value: companyProfile.email,
    href: `mailto:${companyProfile.email}?subject=Support%20i%20Obsero%20Core`,
    detail: "Send en kort beskrivelse, så finder vi den rette person."
  },
  {
    label: "Telefon",
    value: companyProfile.phone,
    href: companyProfile.phoneHref,
    detail: "Ring på hverdage, hvis sagen haster."
  }
];

const supportOptionLabels = {
  "Teknisk support": "Teknisk support",
  "Produktspørgsmål": "Produktspørgsmål",
  "Brugere og adgang": "Brugere og adgang",
  Dokumenter: "Dokumenter",
  Fakturering: "Fakturering",
  Andet: "Andet"
};

function fieldError(errors, serverErrors, field) {
  return errors[field] || serverErrors[field];
}

function SupportIndex({ support_form = {}, support_resources = [] }) {
  const { props } = usePage();
  const flash = props?.flash || {};
  const initialValues = support_form.values || {};
  const serverErrors = support_form.errors || {};
  const interestOptions = support_form.interest_options || Object.keys(supportOptionLabels);
  const { data, setData, post, processing, errors, transform } = useForm({
    name: initialValues.name || "",
    email: initialValues.email || "",
    phone: initialValues.phone || "",
    organization: initialValues.organization || "",
    interest: initialValues.interest || interestOptions[0] || "Teknisk support",
    message: initialValues.message || ""
  });

  const submitSupportRequest = (event) => {
    event.preventDefault();
    transform((values) => ({ contact_request: values }));
    post(support_path(), {
      preserveScroll: true
    });
  };

  return (
    <>
      <Head title="Support" />

      <section className="workspace-content support-page">
        <div className="support-page__intro">
          <div className="workspace-section__eyebrow">Support</div>
          <h2 className="workspace-section__title">Hvordan kan vi hjælpe?</h2>
          <p className="support-page__copy">
            Skriv til os med spørgsmål om adgang, produkter, dokumenter eller tekniske problemer. Jo mere konkret sagen er beskrevet, desto hurtigere kan vi finde den rigtige løsning.
          </p>
        </div>

        <div className="row g-4 align-items-start">
          <div className="col-12 col-xl-7">
            <article className="card support-card">
              <div className="card-body">
                <div className="workspace-card-eyebrow">Kontaktformular</div>
                <h3 className="workspace-card-title">Send en supportbesked</h3>
                <p className="workspace-card-description">
                  Udfyld formularen, så vender vi tilbage på den e-mailadresse, du angiver.
                </p>

                {flash.notice ? <div className="alert alert-success mt-4 mb-0">{flash.notice}</div> : null}

                <form className="support-form" onSubmit={submitSupportRequest}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="support-name">Navn</label>
                      <input
                        id="support-name"
                        type="text"
                        className="form-control"
                        value={data.name}
                        onChange={(event) => setData("name", event.target.value)}
                        required
                      />
                      {fieldError(errors, serverErrors, "name") ? (
                        <div className="invalid-feedback d-block">{fieldError(errors, serverErrors, "name")}</div>
                      ) : null}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="support-email">E-mail</label>
                      <input
                        id="support-email"
                        type="email"
                        className="form-control"
                        value={data.email}
                        onChange={(event) => setData("email", event.target.value)}
                        required
                      />
                      {fieldError(errors, serverErrors, "email") ? (
                        <div className="invalid-feedback d-block">{fieldError(errors, serverErrors, "email")}</div>
                      ) : null}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="support-phone">Telefon</label>
                      <input
                        id="support-phone"
                        type="text"
                        className="form-control"
                        value={data.phone}
                        onChange={(event) => setData("phone", event.target.value)}
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="support-organization">Organisation</label>
                      <input
                        id="support-organization"
                        type="text"
                        className="form-control"
                        value={data.organization}
                        onChange={(event) => setData("organization", event.target.value)}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold" htmlFor="support-interest">Emne</label>
                      <select
                        id="support-interest"
                        className="form-select"
                        value={data.interest}
                        onChange={(event) => setData("interest", event.target.value)}
                      >
                        {interestOptions.map((option) => (
                          <option key={option} value={option}>
                            {supportOptionLabels[option] || option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold" htmlFor="support-message">Besked</label>
                      <textarea
                        id="support-message"
                        rows={8}
                        className="form-control support-form__textarea"
                        value={data.message}
                        onChange={(event) => setData("message", event.target.value)}
                        placeholder="Beskriv kort, hvad du har brug for hjælp til."
                        required
                      />
                      {fieldError(errors, serverErrors, "message") ? (
                        <div className="invalid-feedback d-block">{fieldError(errors, serverErrors, "message")}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="support-form__footer">
                    <button type="submit" className="btn btn-primary" disabled={processing}>
                      {processing ? "Sender..." : "Send besked"}
                    </button>
                    <p className="support-form__note mb-0">Vi bruger kun oplysningerne til at behandle din supporthenvendelse.</p>
                  </div>
                </form>
              </div>
            </article>
          </div>

          <div className="col-12 col-xl-5">
            <div className="support-page__side">
              <article className="card support-card">
                <div className="card-body">
                  <div className="workspace-card-eyebrow">Direkte kontakt</div>
                  <h3 className="workspace-card-title">Kontakt support</h3>
                  <div className="support-channel-list">
                    {supportChannels.map((channel) => (
                      <a key={channel.label} href={channel.href} className="support-channel">
                        <span className="support-channel__label">{channel.label}</span>
                        <strong>{channel.value}</strong>
                        <span>{channel.detail}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </article>

              <article className="card support-card">
                <div className="card-body">
                  <div className="workspace-card-eyebrow">God support</div>
                  <h3 className="workspace-card-title">Det hjælper os at vide</h3>
                  <ul className="support-resource-list">
                    {support_resources.map((resource) => (
                      <li key={resource}>{resource}</li>
                    ))}
                  </ul>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

SupportIndex.layout = (page) => {
  const { layout_header: layoutHeader } = page.props;

  return (
    <AuthLayout
      header={{
        breadcrumbs: [{ label: layoutHeader?.breadcrumb || "Support" }],
        title: layoutHeader?.title || "Support"
      }}
    >
      {page}
    </AuthLayout>
  );
};

export default SupportIndex;
